import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, User, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tilefix-scheduler/artifacts/c05hbcds_IMG_1809.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isSetup) {
        // Create admin account
        await axios.post(`${API}/admin/setup`, formData);
        setIsSetup(false);
        setError('');
        alert('Admin account created! Please login.');
      } else {
        // Login
        const response = await axios.post(`${API}/admin/login`, formData);
        localStorage.setItem('adminToken', response.data.access_token);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Auth error:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already exists')) {
        setError('Admin already exists. Please login.');
        setIsSetup(false);
      } else if (err.response?.status === 401) {
        setError('Invalid username or password');
      } else {
        setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1f33] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Top Notch Tile LLC" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
        </div>

        {/* Login Form */}
        <div className="bg-[#152a42] rounded-lg border border-[#F7931E]/30 p-8">
          <h2 className="text-xl font-bold text-white mb-6">
            {isSetup ? 'Create Admin Account' : 'Sign In'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-2 text-red-200">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white font-medium mb-2">Username</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fa3b8]" />
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder="Enter username"
                  className="form-input rounded-lg pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fa3b8]" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="Enter password"
                  className="form-input rounded-lg pl-10"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-[#0d1f33] border-t-transparent rounded-full animate-spin" />
                  {isSetup ? 'Creating...' : 'Signing In...'}
                </span>
              ) : (
                isSetup ? 'Create Account' : 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSetup(!isSetup)}
              className="text-[#F7931E] hover:underline text-sm"
            >
              {isSetup ? 'Already have an account? Sign in' : 'First time? Create admin account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
